/**
 * Core types of the deterministic simulation: the player's physical state, the
 * per-tick modifiers that boosts provide, the solver's collision profile and the
 * events a physics step reports.
 */
import type { BoostType } from '../boosts';
import type { Gravity, Mode, OrbColor, PadColor, PortalType, SpeedKey } from '../level/types';

/**
 * Complete physical state of one racer during one attempt. Plain data: copy it
 * with `clonePlayerState`, never share it between runs.
 */
export interface PlayerState {
  /** Ticks since the attempt started (index of the next tick to simulate). */
  tick: number;
  /** Centre of the player's outer box, in blocks. */
  x: number;
  y: number;
  /** Vertical velocity in world space (positive = up), blocks/s. */
  vy: number;
  mode: Mode;
  gravity: Gravity;
  speed: SpeedKey;
  /** Resting on a surface on the feet side (the floor, or the ceiling under reversed gravity). */
  grounded: boolean;
  /** Touching a surface on the head side (a ship sliding along a ceiling, a cube's bonk). */
  headContact: boolean;
  /** Input during the last tick; a press is a released → held change. */
  held: boolean;
  /** Ticks since the last press that has not been used yet, or -1 if there is none. */
  pressAge: number;
  /** Ticks left during which a cube, ball, robot or spider may act after walking off a ledge. */
  coyote: number;
  /**
   * Robot: ticks of jump thrust left (see ROBOT in constants.ts); 0 when it is not thrusting
   * and in every other mode.
   */
  thrust: number;
  /** Mid-air jumps left (double-jump boost); refilled on landing. */
  airJumps: number;
  /**
   * Solver only (always 0 in normal play): ticks until every timing-shifted copy of
   * the run (see CollisionProfile) is guaranteed to be in exactly this state again.
   * Very large while airborne after a jump, flip or orb tap; counts down after landing.
   */
  settle: number;
  /**
   * Ids (indices into `World.triggers`) of pads, orbs and pickups that already fired
   * during this attempt and can still be touched. Treated as immutable: a step that
   * changes it assigns a new array, so clones may share it.
   */
  used: readonly number[];
  alive: boolean;
  /** The finish line (LevelData.length) has been reached. */
  finished: boolean;
  deathCause: DeathCause | null;
}

/** Effects of the active boosts that the physics needs to know about. */
export interface PhysicsModifiers {
  /** Spikes and crashes do not kill; falling out of the world bounces the player back. */
  invulnerable: boolean;
  /** Mid-air jumps available after each landing (cube or robot jump, ball flip, teleport). */
  airJumps: number;
}

export const NO_MODIFIERS: Readonly<PhysicsModifiers> = Object.freeze({
  invulnerable: false,
  airJumps: 0,
});

/**
 * Safety margins used by the level solver to model a human's timing error.
 *
 * A jump, ball flip or orb tap made `d` seconds late shifts the rest of its arc
 * horizontally by `d × speed` blocks. With `margin = m`, a step only survives if every
 * copy shifted by up to ±m would survive too: crash and spike checks use boxes
 * widened by m, landings must happen for every shift, orbs must be pressed while all
 * shifted copies overlap them, a jump needs ground under every shifted copy (no
 * coyote-time take-off), and nothing may be acted on right after a portal or pass one
 * before all copies have landed again (they would enter it in different states; see
 * PlayerState.settle). So any single such action may be off by up to m / speed
 * seconds (checkActionTiming in levelgen verifies exactly that). Ship, UFO and wave
 * additionally get `marginY` vertically (capped by MARGIN_Y_CAP), since their height
 * is steered continuously. Normal play uses EXACT_PROFILE.
 */
export interface CollisionProfile {
  margin: number;
  marginY: number;
}

export const EXACT_PROFILE: Readonly<CollisionProfile> = Object.freeze({ margin: 0, marginY: 0 });

/**
 * Why a run ended. `unsafe` only happens with a non-zero CollisionProfile: some
 * timing-shifted copy of the run would behave differently (solver-only).
 */
export type DeathCause = 'spike' | 'crash' | 'pit' | 'unsafe';

/**
 * ground/air = cube jump (air = double-jump boost), flap = UFO, flip = ball gravity flip,
 * robot = the robot's thrust jump (from the ground or, with the double jump, in the air),
 * teleport = the spider's jump to the other side (PlayerSim.prevY: where it came from).
 */
export type JumpKind = 'ground' | 'air' | 'flap' | 'flip' | 'robot' | 'teleport';

/**
 * Something noteworthy that happened during a physics step. `object` is the index
 * of the involved object in `LevelData.objects`.
 */
export type PhysicsEvent =
  | { type: 'jump'; kind: JumpKind }
  /** The feet touched a surface after being airborne (also ship/wave touching a floor). */
  | { type: 'land' }
  | { type: 'orb'; color: OrbColor; object: number }
  | { type: 'pad'; color: PadColor; object: number }
  | { type: 'portal'; portal: PortalType; object: number }
  | { type: 'pickup'; boost: BoostType; object: number }
  | { type: 'death'; cause: DeathCause; x: number; y: number }
  /** The player's centre reached the finish line. */
  | { type: 'finish' };
